import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Inbox, Sparkles, GraduationCap, MapPin, MessagesSquare, Image as ImageIcon, UserCog } from "lucide-react";
import { api } from "@/lib/api";
import LeadsPanel from "@/components/admin/LeadsPanel";
import HeroPanel from "@/components/admin/HeroPanel";
import {
  CoursesCMS,
  BranchesCMS,
  TestimonialsCMS,
  GalleryCMS,
} from "@/components/admin/EntityForms";
import AccountPanel from "@/components/admin/AccountPanel";

const TABS = [
  { id: "leads", label: "Leads", icon: Inbox },
  { id: "hero", label: "Hero", icon: Sparkles },
  { id: "courses", label: "Cursos", icon: GraduationCap },
  { id: "branches", label: "Sucursales", icon: MapPin },
  { id: "testimonials", label: "Testimonios", icon: MessagesSquare },
  { id: "gallery", label: "Galería", icon: ImageIcon },
  { id: "account", label: "Cuenta", icon: UserCog },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("leads");
  const [user, setUser] = useState(null);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    if (!localStorage.getItem("ie_token")) {
      nav("/admin/login");
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => nav("/admin/login"));
    // Load branches once for course price editor
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch branches if we touch the branches tab (in case they change)
  useEffect(() => {
    if (tab === "courses" || tab === "branches") {
      api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    }
  }, [tab]);

  const logout = () => {
    localStorage.removeItem("ie_token");
    localStorage.removeItem("ie_user");
    nav("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#fafafa]">
      {/* Top bar */}
      <header className="bg-white border-b border-black/8 sticky top-0 z-30">
        <div className="container-ie h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="dot-red" />
            <span className="font-display font-bold tracking-tight text-sm">
              INSTITUTO <span className="text-[#c40000]">EUROPA</span>
            </span>
            <span className="ml-3 text-xs uppercase tracking-[0.2em] text-[#525252] hidden sm:inline">
              · Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            {user?.email && (
              <span className="text-xs text-[#525252] hidden md:inline">
                {user.email}
              </span>
            )}
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[#0a0a0a] hover:text-[#c40000]"
            >
              <LogOut size={14} strokeWidth={1.5} /> Salir
            </button>
          </div>
        </div>

        {/* Tabs */}
        <nav className="container-ie border-t border-black/5 overflow-x-auto" data-testid="admin-tabs">
          <ul className="flex gap-1 -mb-px min-w-max">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <li key={t.id}>
                  <button
                    data-testid={`admin-tab-${t.id}`}
                    onClick={() => setTab(t.id)}
                    className={`relative inline-flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.18em] font-semibold transition-colors ${
                      active
                        ? "text-[#0a0a0a]"
                        : "text-[#525252] hover:text-[#0a0a0a]"
                    }`}
                  >
                    <Icon size={14} strokeWidth={1.6} />
                    {t.label}
                    {active && (
                      <span className="absolute left-3 right-3 bottom-0 h-[2px] bg-[#c40000]" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </header>

      <main className="container-ie py-8 md:py-10">
        {tab === "leads" && (
          <LeadsPanel onUnauthorized={() => nav("/admin/login")} />
        )}
        {tab === "hero" && <HeroPanel />}
        {tab === "courses" && <CoursesCMS branches={branches} />}
        {tab === "branches" && <BranchesCMS />}
        {tab === "testimonials" && <TestimonialsCMS />}
        {tab === "gallery" && <GalleryCMS />}
        {tab === "account" && <AccountPanel user={user} />}
      </main>
    </div>
  );
}
